import classnames from 'classnames'
import { useEffect, type CSSProperties } from 'react'
import { Outlet, useLoaderData, useNavigation, type LoaderFunctionArgs, type MetaFunction } from 'react-router'
import { ArchivedWebshop } from '~/components/ArchivedWebshop'
import { CookieBanner } from '~/components/cookie-consent/cookie-banner'
import type { Product } from '~/schemas/products'
import type { CartSnapshot } from '~/services/cart-view.server'
import type { ProductPrice } from '~/services/product-pricing.server'
import type { OrgStyling } from '~/services/styling.server'
import { stylingToCssVars } from '~/utils/styling-vars'
import Footer from './components/footer'
import Menu from './components/menu'

export type SelectedB2BCustomer = {
  id: string
  companyName: string | null
  firstName: string
  lastName: string
} | null

declare module 'react-router' {
  interface RouteHandle {
    isAdmin?: boolean
  }
}

export type Category = {
  key: string
  label: string
}

export type LayoutContext = {
  snapshot: CartSnapshot
  products: Product[]
  styling: OrgStyling
  selectedB2BCustomer: SelectedB2BCustomer
  productPricing: Map<string | number, ProductPrice>
  categories: Category[]
  categoriesEnabled: boolean
  isShopOpen: boolean
  canBypassClosedShop: boolean
}

export const id = 'layout'
export const handle = { isAdmin: true }

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  // Handle archived webshop case
  if (data && 'isArchived' in data && data.isArchived) {
    const title = data.orgName ? `${data.orgName} - Gesloten` : 'Webshop gesloten'
    return [{ title }, { name: 'description', content: 'Deze webshop is gesloten.' }]
  }

  const org = data?.orgDetails
  const title = `${org?.companyName} webshop`
  const description = org?.description
  const canonicalUrl = data?.canonicalUrl

  // JSON-LD Organization schema for AI/LLM search optimization
  const organizationJsonLd = org
    ? {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: org.companyName,
        description: org.description,
        url: canonicalUrl,
        telephone: org.telephone,
        email: org.email,
        address: {
          '@type': 'PostalAddress',
          streetAddress: `${org.street} ${org.houseNumber}`,
          postalCode: org.postalCode,
          addressLocality: org.city,
          addressCountry: 'NL'
        },
        ...(org.kvkNumber ? { taxID: org.vatNumber } : {})
      }
    : null

  // JSON-LD WebSite schema with search action
  const websiteJsonLd = canonicalUrl
    ? {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: title,
        url: canonicalUrl
      }
    : null

  return [
    { title },
    { name: 'description', content: description },
    // Canonical URL
    { tagName: 'link', rel: 'canonical', href: canonicalUrl },
    // OpenGraph tags
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: canonicalUrl },
    { property: 'og:site_name', content: title },
    { property: 'og:locale', content: 'nl_NL' },
    // Twitter Card
    { name: 'twitter:card', content: 'summary' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    // JSON-LD structured data
    ...(organizationJsonLd ? [{ 'script:ld+json': organizationJsonLd }] : []),
    ...(websiteJsonLd ? [{ 'script:ld+json': websiteJsonLd }] : [])
  ]
}

export async function loader(args: LoaderFunctionArgs) {
  // Dynamic imports for server-only code
  const [
    { getTenantContext },
    { getCartSnapshot },
    { getOrganizationDetailsByOrgId },
    { getProductsCached },
    { getOrganizationStyling },
    { getSelectedB2BCustomerId },
    { getBulkProductPricing },
    { sql }
  ] = await Promise.all([
    import('~/services/authz.server'),
    import('~/services/cart-view.server'),
    import('~/services/org-details.server'),
    import('~/services/products.cache.server'),
    import('~/services/styling.server'),
    import('~/services/b2b-customer-session.server'),
    import('~/services/product-pricing.server'),
    import('~/db/connect')
  ])

  // Build canonical URL from request (domain-specific for multi-tenant)
  const url = new URL(args.request.url)
  const canonicalUrl = url.origin

  const isAdminAreaByHandle = args.context.matches?.some((m: { route: { handle?: { isAdmin?: boolean } } }) => m.route.handle?.isAdmin) ?? false
  const isAdminAreaByPath = url.pathname.startsWith('/webshop-admin')

  const tc = await getTenantContext(args)

  // Check if organization is archived (superadmins can bypass)
  if (tc.org?.isArchived && !tc.isAdmin) {
    return {
      isArchived: true,
      orgName: tc.org.displayName
    }
  }

  const styling = await getOrganizationStyling(tc.org?.id)
  const orgDetails = await getOrganizationDetailsByOrgId(tc.org?.id)

  // B2B products should be visible to admins and webshop owners
  const canSeeB2B = tc.isAdmin || tc.orgRole === 'webshop_owner'

  // Fetch selected B2B customer for webshop owners
  let selectedB2BCustomer: SelectedB2BCustomer = null
  if (canSeeB2B) {
    const customerId = await getSelectedB2BCustomerId(args.request)
    if (customerId) {
      const [customer] = (await sql`
        SELECT
          id::text AS id,
          company_name AS "companyName",
          firstname AS "firstName",
          lastname AS "lastName"
        FROM b2b_customer
        WHERE id = ${customerId}::uuid
          AND org_id = ${tc.org?.id}::uuid
          AND archived = false
        LIMIT 1
      `) as Array<{ id: string; companyName: string | null; firstName: string; lastName: string }>

      if (customer) {
        selectedB2BCustomer = customer
      }
    }
  }

  const snapshot = await getCartSnapshot(args.request, canSeeB2B, tc.org?.id)
  const products = await getProductsCached({ organizationId: tc.org?.id, includingB2B: canSeeB2B })

  // Extract unique categories from products (preserving order of first appearance)
  const categoryMap = new Map<string, Category>()
  for (const p of products) {
    if (!categoryMap.has(p.category.key)) {
      categoryMap.set(p.category.key, { key: p.category.key, label: p.category.label })
    }
  }
  const categories = Array.from(categoryMap.values())

  // Fetch B2B pricing for all products if a B2B customer is selected
  const productPricing = await getBulkProductPricing({
    products: products.map(p => ({ id: p.id, defaultPrice: p.price?.amount ?? 0 })),
    b2bCustomerId: selectedB2BCustomer?.id
  })

  // Shop is open for sales? (admins/owners can always see the shop to manage it)
  const isShopOpen = tc.org?.isOpen ?? true

  return {
    admin: tc,
    cartCount: snapshot.total_qty,
    cartItems: snapshot.items.map(i => ({ product_id: i.product_id, qty: i.qty })),
    snapshot,
    products,
    productPricing,
    selectedB2BCustomer,
    isAdminArea: isAdminAreaByHandle || isAdminAreaByPath,
    isB2B: canSeeB2B,
    isShopOpen,
    canBypassClosedShop: tc.canAccessDashboard, // admins/owners can still order when closed
    styling,
    orgDetails,
    categories,
    canonicalUrl
  }
}

// PERFORMANCE OPTIMIZATION: Prevent unnecessary refetches when navigating within admin area
// Cart and products don't need to reload when moving between admin pages
export function shouldRevalidate({ currentUrl, nextUrl, defaultShouldRevalidate }: any) {
  // Don't refetch when navigating within admin area
  const isCurrentAdmin = currentUrl.pathname.startsWith('/webshop-admin')
  const isNextAdmin = nextUrl.pathname.startsWith('/webshop-admin')

  if (isCurrentAdmin && isNextAdmin) {
    return false // Skip revalidation when staying in admin
  }

  return defaultShouldRevalidate
}

export default function WebsiteLayout() {
  const data = useLoaderData<typeof loader>()
  const navigation = useNavigation()

  const isArchived = 'isArchived' in data && data.isArchived
  const favicon = !isArchived ? data.styling?.favicon : null

  // Dynamically set favicon from external domain
  useEffect(() => {
    if (!favicon) return

    // Remove existing favicon links
    const existingLinks = document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]')
    existingLinks.forEach(link => link.remove())

    // Create new favicon link
    const link = document.createElement('link')
    link.rel = 'icon'
    link.href = favicon
    document.head.appendChild(link)
  }, [favicon])

  // Handle archived webshop (after hooks)
  if (isArchived || !data.styling) {
    return <ArchivedWebshop orgName={'orgName' in data ? data.orgName : ''} />
  }

  // Check if admin is viewing an archived webshop
  const isAdminViewingArchived = data.admin?.isAdmin && data.admin?.org?.isArchived

  return (
    <div
      className="ws-theme flex min-h-screen flex-col bg-(--ws-bg)"
      data-ws-mode={data.styling.textColor}
      style={{
        ...stylingToCssVars(data.styling),
        ...(data.isAdminArea ? ({ '--ws-bg': '#f9fafb' } as CSSProperties) : {})
      }}>
      {/* Voortgangsbalkje tijdens navigaties (loaders zijn blocking) */}
      {navigation.state !== 'idle' && <div className="ws-progress" />}
      {isAdminViewingArchived && (
        <>
          <div className="fixed top-0 right-0 left-0 z-[100] bg-red-600 px-4 py-2 text-center text-sm font-medium text-white">
            Deze webshop is gearchiveerd. Alleen admins kunnen deze pagina bekijken.
          </div>
          <div className="h-9" />
        </>
      )}
      <Menu
        cartCount={data.cartCount ?? 0}
        isAdminArea={data.isAdminArea}
        styling={data.styling}
        companyName={data.orgDetails!.companyName}
        selectedB2BCustomer={data.selectedB2BCustomer}
        categories={data.categories}
        categoriesEnabled={data.orgDetails?.categoriesEnabled ?? true}
        hasArchivedBanner={isAdminViewingArchived}
      />
      <div className={classnames('mx-auto w-full flex-1 pt-20 pb-4')}>
        <Outlet
          context={{
            snapshot: data.snapshot,
            products: data.products,
            styling: data.styling,
            selectedB2BCustomer: data.selectedB2BCustomer,
            productPricing: data.productPricing,
            categories: data.categories,
            categoriesEnabled: data.orgDetails?.categoriesEnabled ?? true,
            isShopOpen: data.isShopOpen,
            canBypassClosedShop: data.canBypassClosedShop
          }}
        />
      </div>
      <Footer styling={data.styling} organizationDetails={data.orgDetails!} />
      <CookieBanner />
    </div>
  )
}
